import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'

import { api } from '../api/client'
import type { EagleCalendario, EagleContatto, EagleGruppo, EagleNotifyTutto, EagleSLA } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * Eagle → Notifiche (ADR-0022): chi avvisare, quando, con quale impegno.
 *
 * Quattro anagrafiche in una pagina, nell'ordine in cui si compilano: prima i
 * **contatti** (le persone), poi gli **orari di lavoro** (quando si è in
 * ufficio), poi i **gruppi di notifica** che mettono insieme le due cose con
 * le gravità e i canali, infine gli **SLA**. Il CI sceglie gruppo e SLA nella
 * sua sezione Eagle.
 */
export function EagleNotifiche({ puoModificare }: { puoModificare: boolean }) {
  const dati = useAsync(() => api.eagleNotify(), [])
  const [tutto, setTutto] = useState<EagleNotifyTutto | null>(null)
  const d = tutto ?? dati.data
  if (dati.loading && !d) return <Spinner />
  if (!d || !d.licensed) {
    return <EmptyState title={t('Pagina non disponibile')} hint={t('Questa funzione non è attiva per il tuo spazio.')} />
  }
  const aggiorna = (nuovo: EagleNotifyTutto) => setTutto(nuovo)
  return (
    <>
      <ErrorBanner error={dati.error} />
      <Contatti tutto={d} onCambia={aggiorna} puoModificare={puoModificare} />
      <Calendari tutto={d} onCambia={aggiorna} puoModificare={puoModificare} />
      <Gruppi tutto={d} onCambia={aggiorna} puoModificare={puoModificare} />
      <Slas tutto={d} onCambia={aggiorna} puoModificare={puoModificare} />
    </>
  )
}

type Props = { tutto: EagleNotifyTutto; onCambia: (t: EagleNotifyTutto) => void; puoModificare: boolean }

/** Il giro comune: salva (nuovo o modifica) e rilegge tutto. */
function useSalva(onCambia: (t: EagleNotifyTutto) => void) {
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)
  async function esegui(fn: () => Promise<unknown>) {
    setInvio(true)
    setErrore(null)
    try {
      await fn()
      onCambia(await api.eagleNotify())
      return true
    } catch (e) {
      setErrore(e)
      return false
    } finally {
      setInvio(false)
    }
  }
  return { errore, invio, esegui }
}

// ------------------------------------------------------------- contatti

function Contatti({ tutto, onCambia, puoModificare }: Props) {
  const { errore, invio, esegui } = useSalva(onCambia)
  const [aperto, setAperto] = useState<EagleContatto | 'nuovo' | null>(null)
  const [form, setForm] = useState({ name: '', email: '', phone: '', note: '' })
  const apri = (c?: EagleContatto) => {
    setForm({ name: c?.name ?? '', email: c?.email ?? '', phone: c?.phone ?? '', note: c?.note ?? '' })
    setAperto(c ?? 'nuovo')
  }
  async function salva(e: FormEvent) {
    e.preventDefault()
    const ok = await esegui(() => aperto === 'nuovo' || !aperto
      ? api.eagleNotifyCrea('contacts', form)
      : api.eagleNotifyAggiorna('contacts', aperto.id, form))
    if (ok) setAperto(null)
  }
  // Gli utenti della console non ancora in rubrica: si aggiungono con una scelta.
  const giaContatti = new Set(tutto.contacts.map((c) => c.user_id).filter(Boolean))
  const utentiLiberi = (tutto.users ?? []).filter((u) => !giaContatti.has(u.id))
  const utenteCollegato = aperto && aperto !== 'nuovo' ? aperto.user_id : null
  return (
    <Card title={t('Contatti ({n})', { n: tutto.contacts.length })}
          actions={puoModificare && !aperto ? (
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {utentiLiberi.length > 0 && (
                <select value="" aria-label={t('Aggiungi un utente della console')} disabled={invio}
                        onChange={(e) => { if (e.target.value) void esegui(() => api.eagleNotifyCrea('contacts', { user_id: e.target.value })) }}>
                  <option value="">{t('Aggiungi un utente della console…')}</option>
                  {utentiLiberi.map((u) => <option key={u.id} value={u.id}>{u.display_name ? `${u.display_name} · ${u.email}` : u.email}</option>)}
                </select>
              )}
              <button type="button" className="secondary" onClick={() => apri()}>{t('Nuovo contatto')}</button>
            </span>
          ) : undefined}>
      <p className="muted" style={{ marginTop: 0 }}>{t('I contatti da notificare possono essere utenti CENSO o esterni al sistema. Per gli utenti della console, nome ed e-mail vengono acquisiti automaticamente.')}</p>
      <ErrorBanner error={errore} />
      {aperto && (
        <form onSubmit={salva} className="eagle-form">
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required={!utenteCollegato} /></label>
          <label className="field"><span className="field__label">{t('Email')}</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={Boolean(utenteCollegato)} />
            {utenteCollegato && <span className="muted eagle-piccolo">{t('È l’email con cui l’utente entra in CENSO: si cambia dalla pagina Utenti.')}</span>}</label>
          <label className="field"><span className="field__label">{t('Telefono')}</span>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
          <label className="field"><span className="field__label">{t('Nota')}</span>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
          <div className="eagle-form__azioni">
            <button type="submit" disabled={invio}>{invio ? t('Salvo…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={invio}>{t('Annulla')}</button>
          </div>
        </form>
      )}
      {tutto.contacts.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>{t('Nessun contatto ancora.')}</p>
      ) : (
        <DataTable
          rows={tutto.contacts}
          rowKey={(c) => c.id}
          conteggio={{ nome: t('contatti') }}
          columns={[
            {
              key: 'name', header: t('Nome'), value: (c) => c.name,
              render: (c) => <>{c.name}{c.user_id && <> <span className="badge badge--info">{t('utente')}</span></>}</>,
            },
            { key: 'email', header: t('Email'), mono: true, value: (c) => c.email ?? '', render: (c) => c.email ?? '—' },
            { key: 'phone', header: t('Telefono'), mono: true, value: (c) => c.phone ?? '', render: (c) => c.phone ?? '—' },
            { key: 'note', header: t('Nota'), value: (c) => c.note ?? '', render: (c) => <span className="muted">{c.note ?? ''}</span> },
            ...colonnaAzioni<EagleContatto>(puoModificare, (c) => (
              <Azioni onModifica={() => apri(c)} onElimina={() => void esegui(() => api.eagleNotifyElimina('contacts', c.id))} disabled={invio} />
            )),
          ]}
        />
      )}
    </Card>
  )
}

/** The row actions column, only for who can edit: no name, so always shown. */
function colonnaAzioni<T>(puoModificare: boolean, render: (row: T) => ReactNode): Column<T>[] {
  return puoModificare
    ? [{ key: 'actions', header: <span aria-label={t('Azioni')} />, render, sort: false, filter: false, searchable: false }]
    : []
}

function Azioni({ onModifica, onElimina, disabled }: { onModifica: () => void; onElimina: () => void; disabled: boolean }) {
  return (
    <span style={{ display: 'flex', gap: 8 }}>
      <button type="button" className="secondary" onClick={onModifica} disabled={disabled}>{t('Modifica')}</button>
      <button type="button" className="secondary" disabled={disabled}
              onClick={() => { if (window.confirm(t('Togliere questa voce?'))) onElimina() }}>{t('Togli')}</button>
    </span>
  )
}

// ------------------------------------------------------------ calendari

const GIORNI = () => [t('lun'), t('mar'), t('mer'), t('gio'), t('ven'), t('sab'), t('dom')]

type FormCal = { name: string; timezone: string; always: boolean; slots: { day: number; from: string; to: string }[]; holidays: string }

function Calendari({ tutto, onCambia, puoModificare }: Props) {
  const { errore, invio, esegui } = useSalva(onCambia)
  const [aperto, setAperto] = useState<EagleCalendario | 'nuovo' | null>(null)
  const [form, setForm] = useState<FormCal>({ name: '', timezone: 'Europe/Rome', always: false, slots: [], holidays: '' })
  const apri = (c?: EagleCalendario) => {
    setForm({
      name: c?.name ?? '', timezone: c?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Europe/Rome',
      always: c?.always ?? false,
      slots: c?.slots ?? [0, 1, 2, 3, 4].map((day) => ({ day, from: '08:00', to: '18:00' })),
      holidays: (c?.holidays ?? []).join('\n'),
    })
    setAperto(c ?? 'nuovo')
  }
  async function salva(e: FormEvent) {
    e.preventDefault()
    const corpo = { ...form, holidays: form.holidays.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean) }
    const ok = await esegui(() => aperto === 'nuovo' || !aperto
      ? api.eagleNotifyCrea('calendars', corpo)
      : api.eagleNotifyAggiorna('calendars', aperto.id, corpo))
    if (ok) setAperto(null)
  }
  const giorni = GIORNI()
  return (
    <Card title={t('Orari di lavoro ({n})', { n: tutto.calendars.length })}
          actions={puoModificare && !aperto ? <button type="button" className="secondary" onClick={() => apri()}>{t('Nuovo calendario')}</button> : undefined}>
      <p className="muted" style={{ marginTop: 0 }}>{t('Definisce giorni, fasce orarie e festività in cui il servizio è attivo. Gli SLA si applicano durante l’orario di servizio; fuori orario, il comportamento dipende dal gruppo di notifica.')}</p>
      <ErrorBanner error={errore} />
      {aperto && (
        <form onSubmit={salva} className="eagle-form">
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          <label className="field"><span className="field__label">{t('Fuso orario')}</span>
            <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="mono" /></label>
          <label className="eagle-interruttore">
            <input type="checkbox" checked={form.always} onChange={(e) => setForm({ ...form, always: e.target.checked })} />
            {' '}{t('Sempre (24 ore su 24, 7 giorni su 7)')}
          </label>
          {!form.always && (
            <fieldset className="field">
              <legend className="field__label">{t('Fasce')}</legend>
              {form.slots.map((s, i) => (
                <div key={i} className="eagle-fascia">
                  <select value={s.day} aria-label={t('Giorno')} onChange={(e) => setForm({ ...form, slots: form.slots.map((x, j) => (j === i ? { ...x, day: Number(e.target.value) } : x)) })}>
                    {giorni.map((g, d) => <option key={d} value={d}>{g}</option>)}
                  </select>
                  <input type="time" value={s.from} aria-label={t('Dalle')} onChange={(e) => setForm({ ...form, slots: form.slots.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)) })} />
                  <span className="muted">–</span>
                  <input type="time" value={s.to} aria-label={t('Alle')} onChange={(e) => setForm({ ...form, slots: form.slots.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)) })} />
                  <button type="button" className="linklike" onClick={() => setForm({ ...form, slots: form.slots.filter((_, j) => j !== i) })}>{t('togli')}</button>
                </div>
              ))}
              <button type="button" className="secondary" onClick={() => setForm({ ...form, slots: [...form.slots, { day: 0, from: '08:00', to: '18:00' }] })}>{t('Aggiungi una fascia')}</button>
            </fieldset>
          )}
          <label className="field"><span className="field__label">{t('Festività (una data per riga, AAAA-MM-GG)')}</span>
            <textarea rows={3} value={form.holidays} onChange={(e) => setForm({ ...form, holidays: e.target.value })} className="mono" /></label>
          <div className="eagle-form__azioni">
            <button type="submit" disabled={invio}>{invio ? t('Salvo…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={invio}>{t('Annulla')}</button>
          </div>
        </form>
      )}
      {tutto.calendars.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>{t('Nessun calendario configurato: le notifiche sono sempre attive.')}</p>
      ) : (
        <DataTable
          rows={tutto.calendars}
          rowKey={(c) => c.id}
          conteggio={{ nome: t('calendari') }}
          columns={[
            { key: 'name', header: t('Nome') },
            { key: 'timezone', header: t('Fuso orario'), mono: true },
            { key: 'slots', header: t('Fasce'), value: (c) => (c.always ? t('sempre') : riassuntoFasce(c.slots, giorni)) },
            { key: 'holidays', header: t('Festività'), type: 'number', value: (c) => c.holidays.length, render: (c) => c.holidays.length },
            ...colonnaAzioni<EagleCalendario>(puoModificare, (c) => (
              <Azioni onModifica={() => apri(c)} onElimina={() => void esegui(() => api.eagleNotifyElimina('calendars', c.id))} disabled={invio} />
            )),
          ]}
        />
      )}
    </Card>
  )
}

/** «lun–ven 08:00–18:00» quando le fasce sono uguali, altrimenti l'elenco. */
function riassuntoFasce(slots: EagleCalendario['slots'], giorni: string[]): string {
  if (!slots.length) return '—'
  const orari = new Set(slots.map((s) => `${s.from}–${s.to}`))
  const gg = [...new Set(slots.map((s) => s.day))].sort((a, b) => a - b)
  const consecutivi = gg.every((d, i) => i === 0 || d === gg[i - 1] + 1)
  if (orari.size === 1 && consecutivi && gg.length > 1) return `${giorni[gg[0]]}–${giorni[gg[gg.length - 1]]} ${[...orari][0]}`
  return slots.map((s) => `${giorni[s.day]} ${s.from}–${s.to}`).join(', ')
}

// ---------------------------------------------------------------- gruppi

type FormGruppo = Omit<EagleGruppo, 'id'>

function Gruppi({ tutto, onCambia, puoModificare }: Props) {
  const { errore, invio, esegui } = useSalva(onCambia)
  const [aperto, setAperto] = useState<EagleGruppo | 'nuovo' | null>(null)
  const vuoto: FormGruppo = { name: '', contact_ids: [], channels: { email: true, inapp: true }, severities: ['crit', 'down'], calendar_id: null, outside_hours: 'critical', delay_minutes: 2, repeat_minutes: 0, enabled: true }
  const [form, setForm] = useState<FormGruppo>(vuoto)
  const apri = (g?: EagleGruppo) => {
    setForm(g ? { ...g } : vuoto)
    setAperto(g ?? 'nuovo')
  }
  async function salva(e: FormEvent) {
    e.preventDefault()
    const ok = await esegui(() => aperto === 'nuovo' || !aperto
      ? api.eagleNotifyCrea('groups', form)
      : api.eagleNotifyAggiorna('groups', aperto.id, form))
    if (ok) setAperto(null)
  }
  const nomeContatto = (id: string) => tutto.contacts.find((c) => c.id === id)?.name ?? '?'
  const nomeCal = (id: string | null) => (id ? tutto.calendars.find((c) => c.id === id)?.name ?? '?' : t('sempre'))
  const gravita: { key: 'warn' | 'crit' | 'down'; label: () => string }[] = [
    { key: 'warn', label: () => t('attenzione') }, { key: 'crit', label: () => t('critico') }, { key: 'down', label: () => t('non risponde') },
  ]
  const fuori = [
    { key: 'all', label: () => t('avvisa comunque') },
    { key: 'critical', label: () => t('solo critico e non risponde') },
    { key: 'none', label: () => t('niente, si vede al rientro') },
  ]
  return (
    <Card title={t('Gruppi di notifica ({n})', { n: tutto.groups.length })}
          actions={puoModificare && !aperto ? <button type="button" className="secondary" onClick={() => apri()}>{t('Nuovo gruppo')}</button> : undefined}>
      <p className="muted" style={{ marginTop: 0 }}>{t('Configura destinatari, livelli di gravità, canali e orari delle notifiche. Il gruppo viene associato a ciascun CI monitorato nella sezione Eagle.')}</p>
      <ErrorBanner error={errore} />
      {aperto && (
        <form onSubmit={salva} className="eagle-form">
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          <fieldset className="field"><legend className="field__label">{t('Contatti')}</legend>
            {tutto.contacts.length === 0 && <span className="muted">{t('Crea prima un contatto.')}</span>}
            <div className="eagle-scelta-servizi">
              {tutto.contacts.map((c) => (
                <label key={c.id}><input type="checkbox" checked={form.contact_ids.includes(c.id)}
                  onChange={() => setForm({ ...form, contact_ids: form.contact_ids.includes(c.id) ? form.contact_ids.filter((x) => x !== c.id) : [...form.contact_ids, c.id] })} />{' '}{c.name}<span className="muted mono"> · {c.email ?? c.phone}</span></label>
              ))}
            </div>
          </fieldset>
          <fieldset className="field"><legend className="field__label">{t('Canali')}</legend>
            <label className="eagle-interruttore"><input type="checkbox" checked={!!form.channels.email} onChange={(e) => setForm({ ...form, channels: { ...form.channels, email: e.target.checked } })} />{' '}{t('Email')}</label>{' '}
            <label className="eagle-interruttore"><input type="checkbox" checked={!!form.channels.inapp} onChange={(e) => setForm({ ...form, channels: { ...form.channels, inapp: e.target.checked } })} />{' '}{t('Campanella in console')}</label>
          </fieldset>
          <fieldset className="field"><legend className="field__label">{t('Gravità')}</legend>
            {gravita.map((g) => (
              <label key={g.key} className="eagle-interruttore" style={{ marginRight: 12 }}>
                <input type="checkbox" checked={form.severities.includes(g.key)}
                  onChange={() => setForm({ ...form, severities: form.severities.includes(g.key) ? form.severities.filter((x) => x !== g.key) : [...form.severities, g.key] })} />{' '}{g.label()}
              </label>
            ))}
          </fieldset>
          <label className="field"><span className="field__label">{t('Orari di lavoro')}</span>
            <select value={form.calendar_id ?? ''} onChange={(e) => setForm({ ...form, calendar_id: e.target.value || null })}>
              <option value="">{t('sempre')}</option>
              {tutto.calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></label>
          {form.calendar_id && (
            <label className="field"><span className="field__label">{t('Fuori orario')}</span>
              <select value={form.outside_hours} onChange={(e) => setForm({ ...form, outside_hours: e.target.value as FormGruppo['outside_hours'] })}>
                {fuori.map((f) => <option key={f.key} value={f.key}>{f.label()}</option>)}
              </select></label>
          )}
          <label className="field"><span className="field__label">{t('Attesa prima di avvisare (minuti)')}</span>
            <input type="number" min={0} max={1440} value={form.delay_minutes} onChange={(e) => setForm({ ...form, delay_minutes: Number(e.target.value) })} style={{ width: 100 }} />
            <span className="muted eagle-piccolo">{t('Un problema che rientra da solo entro l’attesa non avvisa nessuno.')}</span></label>
          <label className="field"><span className="field__label">{t('Ripeti ogni (minuti, 0 = mai)')}</span>
            <input type="number" min={0} max={1440} value={form.repeat_minutes} onChange={(e) => setForm({ ...form, repeat_minutes: Number(e.target.value) })} style={{ width: 100 }} /></label>
          <label className="eagle-interruttore"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />{' '}{t('Attivo')}</label>
          <div className="eagle-form__azioni">
            <button type="submit" disabled={invio}>{invio ? t('Salvo…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={invio}>{t('Annulla')}</button>
          </div>
        </form>
      )}
      {tutto.groups.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>{t('Nessun gruppo configurato: le notifiche non vengono inviate.')}</p>
      ) : (
        <DataTable
          rows={tutto.groups}
          rowKey={(g) => g.id}
          rowClassName={(g) => (g.enabled ? undefined : 'eagle-riga--spenta')}
          conteggio={{ nome: t('gruppi') }}
          columns={[
            {
              key: 'name', header: t('Nome'), value: (g) => (g.enabled ? g.name : `${g.name} · ${t('spento')}`),
              render: (g) => <>{g.name}{!g.enabled && <span className="muted"> · {t('spento')}</span>}</>,
            },
            { key: 'contacts', header: t('Contatti'), value: (g) => g.contact_ids.map(nomeContatto).join(', ') },
            { key: 'severities', header: t('Gravità'), value: (g) => gravita.filter((x) => g.severities.includes(x.key)).map((x) => x.label()).join(', ') },
            { key: 'channels', header: t('Canali'), value: (g) => [g.channels.email && t('Email'), g.channels.inapp && t('Campanella in console')].filter(Boolean).join(', ') },
            {
              key: 'calendar', header: t('Orari di lavoro'),
              value: (g) => [nomeCal(g.calendar_id), g.calendar_id ? fuori.find((f) => f.key === g.outside_hours)?.label() : ''].filter(Boolean).join(' · '),
              render: (g) => <>{nomeCal(g.calendar_id)}{g.calendar_id && <span className="muted"> · {fuori.find((f) => f.key === g.outside_hours)?.label()}</span>}</>,
            },
            {
              key: 'delay', header: t('Attesa'), value: (g) => g.delay_minutes,
              render: (g) => <>{t('{n} min', { n: g.delay_minutes })}{g.repeat_minutes > 0 && <span className="muted"> · {t('ripete ogni {n} min', { n: g.repeat_minutes })}</span>}</>,
            },
            ...colonnaAzioni<EagleGruppo>(puoModificare, (g) => (
              <Azioni onModifica={() => apri(g)} onElimina={() => void esegui(() => api.eagleNotifyElimina('groups', g.id))} disabled={invio} />
            )),
          ]}
        />
      )}
    </Card>
  )
}

// ------------------------------------------------------------------- SLA

type FormSLA = Omit<EagleSLA, 'id'>

function Slas({ tutto, onCambia, puoModificare }: Props) {
  const { errore, invio, esegui } = useSalva(onCambia)
  const [aperto, setAperto] = useState<EagleSLA | 'nuovo' | null>(null)
  const vuoto: FormSLA = { name: '', description: '', availability_pct: 99.5, ack_minutes: 30, resolve_minutes: 240, calendar_id: null }
  const [form, setForm] = useState<FormSLA>(vuoto)
  const apri = (s?: EagleSLA) => {
    setForm(s ? { ...s, description: s.description ?? '' } : vuoto)
    setAperto(s ?? 'nuovo')
  }
  async function salva(e: FormEvent) {
    e.preventDefault()
    const ok = await esegui(() => aperto === 'nuovo' || !aperto
      ? api.eagleNotifyCrea('slas', form)
      : api.eagleNotifyAggiorna('slas', aperto.id, form))
    if (ok) setAperto(null)
  }
  const nomeCal = (id: string | null) => (id ? tutto.calendars.find((c) => c.id === id)?.name ?? '?' : t('sempre'))
  return (
    <Card title={t('SLA ({n})', { n: tutto.slas.length })}
          actions={puoModificare && !aperto ? <button type="button" className="secondary" onClick={() => apri()}>{t('Nuovo SLA')}</button> : undefined}>
      <p className="muted" style={{ marginTop: 0 }}>{t('Definisce i livelli di servizio: disponibilità mensile attesa, tempi di presa in carico e risoluzione e relative fasce di validità. Ogni CI monitorato utilizza lo SLA assegnato nella sezione Eagle; lo stesso SLA è il riferimento per i report di disponibilità.')}</p>
      <ErrorBanner error={errore} />
      {aperto && (
        <form onSubmit={salva} className="eagle-form">
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          <label className="field"><span className="field__label">{t('Descrizione')}</span>
            <input value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <label className="field"><span className="field__label">{t('Disponibilità attesa (%)')}</span>
            <input type="number" min={0} max={100} step={0.01} value={form.availability_pct} onChange={(e) => setForm({ ...form, availability_pct: Number(e.target.value) })} style={{ width: 120 }} /></label>
          <label className="field"><span className="field__label">{t('Presa in carico entro (minuti)')}</span>
            <input type="number" min={1} value={form.ack_minutes} onChange={(e) => setForm({ ...form, ack_minutes: Number(e.target.value) })} style={{ width: 120 }} /></label>
          <label className="field"><span className="field__label">{t('Risoluzione entro (minuti)')}</span>
            <input type="number" min={1} value={form.resolve_minutes} onChange={(e) => setForm({ ...form, resolve_minutes: Number(e.target.value) })} style={{ width: 120 }} /></label>
          <label className="field"><span className="field__label">{t('Vale negli orari')}</span>
            <select value={form.calendar_id ?? ''} onChange={(e) => setForm({ ...form, calendar_id: e.target.value || null })}>
              <option value="">{t('sempre')}</option>
              {tutto.calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></label>
          <div className="eagle-form__azioni">
            <button type="submit" disabled={invio}>{invio ? t('Salvo…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={invio}>{t('Annulla')}</button>
          </div>
        </form>
      )}
      {tutto.slas.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>{t('Nessuno SLA ancora.')}</p>
      ) : (
        <DataTable
          rows={tutto.slas}
          rowKey={(s) => s.id}
          conteggio={{ nome: t('SLA') }}
          columns={[
            {
              key: 'name', header: t('Nome'), value: (s) => [s.name, s.description].filter(Boolean).join(' '),
              render: (s) => <>{s.name}{s.description && <div className="muted eagle-piccolo">{s.description}</div>}</>,
            },
            { key: 'availability', header: t('Disponibilità'), type: 'number', mono: true, value: (s) => s.availability_pct, render: (s) => `${s.availability_pct} %` },
            { key: 'ack', header: t('Presa in carico'), type: 'number', value: (s) => s.ack_minutes, render: (s) => t('{n} min', { n: s.ack_minutes }) },
            { key: 'resolve', header: t('Risoluzione'), type: 'number', value: (s) => s.resolve_minutes, render: (s) => t('{n} min', { n: s.resolve_minutes }) },
            { key: 'calendar', header: t('Vale negli orari'), value: (s) => nomeCal(s.calendar_id) },
            ...colonnaAzioni<EagleSLA>(puoModificare, (s) => (
              <Azioni onModifica={() => apri(s)} onElimina={() => void esegui(() => api.eagleNotifyElimina('slas', s.id))} disabled={invio} />
            )),
          ]}
        />
      )}
    </Card>
  )
}
